import * as React from 'react'
import { Row, Col, Form, FormGroup, FormControl, ControlLabel, Button, Checkbox, Grid } from 'react-bootstrap'
import { AccountData } from './SignupContainer'
import LanguageSelect from '../common/LanguageSelect'
import CurrencySelect from '../common/CurrencySelect'
import CustomGoogleSigninButton from '../common/googleLogin/CustomGoogleSigninButton'
import NewMicrosoftSigninButton from '../common/microsoftLogin/NewMicrosoftSigninButton'
import { captureExceptionWithSentry } from '../sentry'
import { microsoftLoginSignupHandler, googleLoginSignupHandler } from '../common/GoogleMicrosoftApiCall'
import { CredentialResponse, TokenResponse } from '@react-oauth/google'

interface SignupProps {
	accountData: AccountData
	setAccountData: (name: keyof AccountData, value: string | boolean) => void
	createAccount: () => void
	creating: boolean
	disableSendBtn: boolean
	language: string | undefined
	embed?: boolean
	serviceName: string
}

const SignupForm: React.FunctionComponent<SignupProps> = (props) => {
	const [read, setRead] = React.useState(false)
	const [accepted, setAccepted] = React.useState(false)
	const [googleMicrosoftData, setGoogleMicrosoftData] = React.useState(false)
	const [googleMicrosoftError, setGoogleMicrosoftError] = React.useState(false)
	const [msClientId, setMsClientId] = React.useState('')
	const [googleClientId, setGoogleClientId] = React.useState('')

	const linkStyle = {
		cursor: 'pointer',
	}

	React.useEffect(() => {
		const root = document.getElementById('root')
		if (root) {
			const msId = root.getAttribute('data-ms-id') ?? ''
			const gid = root.getAttribute('data-google-id') ?? ''
			setMsClientId(msId)
			setGoogleClientId(gid)
		}
	}, [])

	const microsoftLogin = async () => {
		try {
			const { result } = await microsoftLoginSignupHandler(msClientId)
			if (result.status === 'OK' && !result.data) {
				window.location.href = '/dashboard?lgn=1'
			}
			if (result.status === 'OK' && result.data && Object.keys(result.data).length) {
				setGoogleMicrosoftData(true)
				props.setAccountData('signupUsingMedia', true)
				props.setAccountData('username', result.data.username)
				props.setAccountData('firstName', result.data.firstName)
				props.setAccountData('lastName', result.data.lastName)
			}
		} catch (error) {
			captureExceptionWithSentry(error)
			setGoogleMicrosoftError(true)
		}
	}

	const googleLogin = async (response: CredentialResponse | TokenResponse) => {
		try {
			const resp = await googleLoginSignupHandler(response)
			if (resp.status === 'OK' && !resp.data) {
				window.location.href = '/dashboard?lgn=1'
			}
			if (resp.status === 'OK' && resp.data && Object.keys(resp.data).length) {
				setGoogleMicrosoftData(true)
				props.setAccountData('signupUsingMedia', true)
				props.setAccountData('username', resp.data.username)
				props.setAccountData('firstName', resp.data.firstName)
				props.setAccountData('lastName', resp.data.lastName)
				props.setAccountData('org', resp.data.company!)
			}
		} catch (error) {
			captureExceptionWithSentry(error)
			setGoogleMicrosoftError(true)
		}
	}

	return (
		<div className="shed-form__wrapper">
			<Grid>
				<Row>
					<Form className="shed-form">
						{msClientId && (
							<>
								<NewMicrosoftSigninButton
									microsoftLogin={microsoftLogin}
									text="Sign up with Microsoft"
								/>

								<div className="shed-form__divider">
									<span>or</span>
								</div>
							</>
						)}
						{googleClientId && (
							<CustomGoogleSigninButton
								clientId={googleClientId}
								googleLogin={googleLogin}
								onError={setGoogleMicrosoftError}
							/>
						)}

						{googleMicrosoftError && (
							<div className="alert alert-danger ">
								<button className="close" data-close="alert"></button>
								<span>Error happened while signing up with Google/Microsoft</span>
							</div>
						)}
						<FormGroup style={{ marginTop: '50px' }} className="shed-form__control" controlId="signupEmail">
							<ControlLabel>{props.language === 'fi' ? 'Sähköposti' : 'Email'}</ControlLabel>
							<FormControl
								type="email"
								value={props.accountData.username}
								disabled
								onChange={(e: React.FormEvent<FormControl> & { target: HTMLInputElement }) =>
									props.setAccountData('username', e.target.value)
								}
							/>
						</FormGroup>
						<Col className="shed-form__group">
							<FormGroup className="shed-form__control" controlId="firstName">
								<ControlLabel>{props.language === 'fi' ? 'Etunimi' : 'First Name'}</ControlLabel>
								<FormControl
									type="text"
									value={props.accountData.firstName}
									onChange={(e: React.FormEvent<FormControl> & { target: HTMLInputElement }) =>
										props.setAccountData('firstName', e.target.value)
									}
								/>
							</FormGroup>

							<FormGroup className="shed-form__control" controlId="lastName">
								<ControlLabel>{props.language === 'fi' ? 'Sukunimi' : 'Last Name'}</ControlLabel>
								<FormControl
									type="text"
									value={props.accountData.lastName}
									onChange={(e: React.FormEvent<FormControl> & { target: HTMLInputElement }) =>
										props.setAccountData('lastName', e.target.value)
									}
								/>
							</FormGroup>
						</Col>
						<FormGroup className="shed-form__control" controlId="signupCompanyName">
							<ControlLabel>{props.language === 'fi' ? 'Yrityksen nimi' : 'Company name'}</ControlLabel>
							<FormControl
								type="text"
								value={props.accountData.org}
								onChange={(e: React.FormEvent<FormControl> & { target: HTMLInputElement }) =>
									props.setAccountData('org', e.target.value)
								}
							/>
						</FormGroup>

						<Row>
							<Col md={6}>
								<FormGroup className="shed-form__control" controlId="language">
									<ControlLabel>{props.language === 'fi' ? 'Kieli' : 'Language'}</ControlLabel>
									<LanguageSelect
										language={props.accountData.language}
										languageChanged={(lang: string) => props.setAccountData('language', lang)}
									/>
								</FormGroup>
							</Col>

							<Col md={6}>
								<FormGroup className="shed-form__control" controlId="currency">
									<ControlLabel>{props.language === 'fi' ? 'Valuutta' : 'Currency'}</ControlLabel>
									<CurrencySelect
										noApi={true}
										currency={props.accountData.currency}
										currencyChanged={(currency: string) =>
											props.setAccountData('currency', currency)
										}
									/>
								</FormGroup>
							</Col>
						</Row>

						<FormGroup controlId="tos">
							<Checkbox checked={read} onChange={() => setRead(!read)}>
								{props.language === 'fi' ? (
									<>
										Olen lukenut ja ymmärrän{' '}
										<a
											href="https://readpeak.com/terms-of-service"
											target="_blank"
											rel="noreferrer"
										>
											käyttöehdot
										</a>
									</>
								) : (
									<>
										I have read and understand the{' '}
										<a
											href="https://readpeak.com/terms-of-service"
											target="_blank"
											rel="noreferrer"
										>
											terms of service
										</a>
									</>
								)}
							</Checkbox>
							<Checkbox checked={accepted} onChange={() => setAccepted(!accepted)}>
								{props.language === 'fi' ? (
									<>
										Hyväksyn{' '}
										<a
											href="https://readpeak.com/terms-of-service"
											target="_blank"
											rel="noreferrer"
										>
											käyttöehdot
										</a>
									</>
								) : (
									<>
										I accept the{' '}
										<a
											href="https://readpeak.com/terms-of-service"
											target="_blank"
											rel="noreferrer"
										>
											terms of service
										</a>
									</>
								)}
							</Checkbox>
						</FormGroup>
					</Form>
				</Row>

				<Row>
					<Button
						bsStyle="primary"
						onClick={() => props.createAccount()}
						block
						disabled={props.creating || props.disableSendBtn || !read || !accepted}
					>
						{props.language === 'fi'
							? props.creating
								? 'LUODAAN TILIÄ...'
								: 'REKISTERÖIDY'
							: props.creating
							? 'CREATING ACCOUNT...'
							: 'GET STARTED'}
					</Button>
				</Row>
				{!props.embed && (
					<div>
						<Row>&nbsp;</Row>
						<Row>&nbsp;</Row>

						<Row>
							<Col mdOffset={3} md={6}>
								<h4 className="text-center">
									{props.language === 'fi'
										? `Onko sinulla jo ${props.serviceName}-tunnus? `
										: `Already have a ${props.serviceName} Account? `}
									<span
										className="text-info"
										style={linkStyle}
										onClick={() => (window.location.href = '/login')}
									>
										{props.language === 'fi' ? 'Kirjaudu sisään tästä!' : 'Login here!'}
									</span>
								</h4>
							</Col>
						</Row>
					</div>
				)}
			</Grid>
		</div>
	)
}

export default SignupForm
